import md5 from 'md5';
import { getAPIHost } from '@rsos/base-utils/metaTags';
import { JURISDICTION_VIEW } from './constants/internalConstants';
import type {
  UserData,
  AccountData,
  NormalizedUserInfo,
  NormalizedAccountInfo,
} from './types';
import { safeParseDate, getGlobalEnvVar } from './utils';

/**
 * Normalizes user information for Amplitude identify properties.
 * This function handles both authenticated and unauthenticated users.
 *
 * For authenticated users, provide a full UserData object.
 * For unauthenticated users, you can provide a minimal object with just a profileId:
 * @example
 * // For unauthenticated users
 * normalizeUserInfo({ profileId: 'anonymous-123' })
 *
 * @throws {Error} If required user data is missing
 */
export function normalizeUserInfo(userData: UserData): NormalizedUserInfo {
  // Validate input
  if (!userData) {
    throw new Error('Invalid user data: object is required');
  }
  if (!userData.profileId) {
    userData.profileId = 'unknown';
  }

  // Base fields for all users. Amplitude applies its own env suffix to user ids
  // via getAmplitudeEnvironmentSuffix; keep the normalized id as the raw profileId.
  const baseFields = {
    appVersion: getGlobalEnvVar('REACT_APP_FE_VERSION') ?? '',
    host: getAPIHost(),
    id: String(userData.profileId),
  };

  // If this is an unauthenticated user with just a profileId, return basic info
  if (Object.keys(userData).length === 1 && userData.profileId) {
    return baseFields as NormalizedUserInfo;
  }

  // For authenticated users, normalize all fields according to standards:
  // - String fields: null for missing values
  // - Date fields: null for missing values (handled by safeParseDate)
  return {
    ...baseFields,
    appName: userData.application ?? null,
    email: userData.email ?? null,
    firstName: userData.firstName ?? null,
    hashedEmail: userData.email
      ? md5(userData.email.toLowerCase().trim())
      : null,
    lastActive: safeParseDate(userData.lastActiveSession),
    lastName: userData.lastName ?? null,
    signUpDate: safeParseDate(userData.dateJoined),
    userRole: userData.role ?? null,
  };
}

/**
 * Normalizes account information for Amplitude identify properties.
 */
export function normalizeAccountInfo(
  accountData: AccountData,
): NormalizedAccountInfo {
  // Handle null/undefined accountData
  if (!accountData) {
    throw new Error('Invalid account data: object is required');
  }

  return {
    id: accountData.accountId ?? null,
    name: accountData.displayName ?? null,
    PrimaryPointOfContact: accountData.contactEmail ?? null,
    JurisdictionViewEnabled: !!(
      accountData.capabilities?.[JURISDICTION_VIEW] ?? false
    ),
    AddressablePopulation: accountData.population ?? 0,
    AgencyState: accountData.state ?? null,
    DispatchType: accountData.dispatchType ?? null,
  };
}
